import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import 'react';
import Style from './style.js';
import { jsx } from '@emotion/react';
import '../../constants/breakpoints/index.js';
import '../../constants/grid/index.js';
import '../../constants/mediaQueries/index.js';
import '../../constants/boxShadows/primary.js';
import '../../constants/boxShadows/secondary.js';
import CheckmarkIcon from '../../icons/icons/checkmarkIcon.js';
import { isDefined } from '../../utils/isDefined/index.js';

var _excluded = ["borderRadius", "buttonType", "icon", "onClick", "optionType", "selected", "subtext", "text", "image", "textContainerHeight", "alt"],
    _excluded2 = ["borderRadius", "icon", "optionType", "subtext", "text", "image"];
var DEFAULT_BORDER_RADIUS = 'small';
var OptionButtonIcon = _ref => {
  var {
    buttonType = 'primary',
    icon,
    optionType,
    selected = false,
    withImageBackground = false
  } = _ref;

  if (isDefined(icon) && icon !== false) {
    return jsx(Style.IconWrapper, {
      selected: selected,
      optionType: optionType,
      buttonType: buttonType,
      withImageBackground: withImageBackground
    }, selected ? jsx(CheckmarkIcon, null) : icon);
  }

  return jsx(Style.CheckmarkWrapper, {
    selected: selected,
    optionType: optionType,
    buttonType: buttonType,
    withImageBackground: withImageBackground
  }, jsx(CheckmarkIcon, null));
};

var OptionButtonContent = _ref2 => {
  var {
    buttonType = 'primary',
    icon,
    optionType,
    selected = false,
    subtext,
    text,
    image = '',
    borderRadius = DEFAULT_BORDER_RADIUS,
    textContainerHeight,
    alt = ''
  } = _ref2;
  return jsx(Style.FlexContainer, {
    containsImage: !!image
  }, image ? jsx(Style.ImageContainer, {
    borderRadius: borderRadius
  }, jsx(OptionButtonIcon, {
    selected: selected,
    optionType: optionType,
    buttonType: buttonType,
    icon: icon,
    withImageBackground: true
  }), jsx(Style.Image, {
    src: image,
    borderRadius: borderRadius,
    alt: alt
  })) : jsx(OptionButtonIcon, {
    selected: selected,
    optionType: optionType,
    buttonType: buttonType,
    icon: icon
  }), jsx(Style.TextContainer, {
    containsImage: !!image,
    height: textContainerHeight
  }, jsx(Style.Text, {
    bold: !!image
  }, text), isDefined(subtext) && jsx(Style.SubText, null, subtext)));
};
/**
 * The `OptionButton` is used within a focused flow (such as the signup page)
 * to provide more context and attention to each selection option.
 *
 * There is also an OptionButton.Container sub-component to be used when we
 * want to use the OptionButton as a purely presentational component rather
 * than a functional button associated with form inputs
 */


var OptionButton = _ref3 => {
  var {
    borderRadius = DEFAULT_BORDER_RADIUS,
    buttonType,
    icon,
    onClick,
    optionType,
    selected,
    subtext,
    text,
    image = '',
    textContainerHeight,
    alt = ''
  } = _ref3,
      rest = _objectWithoutProperties(_ref3, _excluded);

  return jsx(Style.ClickableContainer, _extends({
    borderRadius: borderRadius,
    onClick: onClick,
    type: "button",
    role: optionType,
    "aria-checked": selected,
    containerType: "clickable",
    containsImage: !!image // eslint-disable-next-line react/jsx-props-no-spreading

  }, rest), jsx(OptionButtonContent, {
    buttonType: buttonType,
    icon: icon,
    optionType: optionType,
    selected: selected,
    subtext: subtext,
    text: text,
    image: image,
    borderRadius: borderRadius,
    textContainerHeight: textContainerHeight,
    alt: alt
  }));
};
/**
 * A presentational component to match the display of an OptionButton with an icon
 */

var OptionButtonNotClickable = _ref4 => {
  var {
    borderRadius = DEFAULT_BORDER_RADIUS,
    icon,
    optionType,
    subtext,
    text,
    image = ''
  } = _ref4,
      rest = _objectWithoutProperties(_ref4, _excluded2);

  return jsx(Style.DisplayContainer, _extends({
    borderRadius: borderRadius,
    containerType: "none" // eslint-disable-next-line react/jsx-props-no-spreading

  }, rest), jsx(OptionButtonContent // The buttonType does not matter for this component
  , {
    buttonType: "primary",
    icon: icon,
    optionType: optionType,
    selected: false,
    subtext: subtext,
    text: text,
    image: image
  }));
};
/**
 * Similar OptionButton styling without click elements
 */

OptionButton.NotClickable = OptionButtonNotClickable;

export { OptionButton, OptionButtonIcon, OptionButtonNotClickable };
